import React from 'react'
import Item from '../__item/searchbar__item'
import { cn } from 'ethcss'
import styles from './autocomplete.jcss'
import translate from 'core/translate'

export type AutocompleteItemType = 'places' | 'displays' | 'digitalSignageGroup' | 'digitalSignage' | 'broadcast'

export interface AutocompleteItem {
    id?: number
    __type?: AutocompleteItemType
    address?: string
    lat?: number
    lng?: number
    name?: string
    title?: string
    createdAt?: string
    coverSource?: {
        fileType?: 'video' | 'audio' | 'web_app' | 'web_page' | 'office'
        thumbnail: string
    }
    [key: string]: unknown
}

interface AutocompleteDataSource {
    digitalSignageGroup: AutocompleteItem[]
    digitalSignage: AutocompleteItem[]
    broadcast: AutocompleteItem[]
}

export interface IAutocompleteProps extends Partial<AutocompleteDataSource> {
    displays?: AutocompleteItem[]
    onSelect: (element: AutocompleteItem, type: AutocompleteItemType) => void
    query?: Record<string, string>
    isOpen: boolean
    places?: AutocompleteItem[]
    searchMap?: boolean
}

const historyLength = 15
const history: AutocompleteDataSource = {
    digitalSignageGroup: [],
    digitalSignage: [],
    broadcast: [],
}

const allHistory: AutocompleteItem[] = []

const isHistoryType = (type: AutocompleteItemType): type is keyof AutocompleteDataSource =>
    type !== 'places' && type !== 'displays'

const Autocomplete = (p_: IAutocompleteProps) => {
    const query = p_.query ?? { query: '', name: '' }
    const displays = p_.displays ?? []
    const places = p_.places ?? []
    const currentDataSource: AutocompleteDataSource = {
        digitalSignageGroup: p_.digitalSignageGroup ?? [],
        digitalSignage: p_.digitalSignage ?? [],
        broadcast: p_.broadcast ?? [],
    }
    const dataSource = query.name ? currentDataSource : history

    const getSearchStatus = (data: AutocompleteDataSource) => {
        return {
            digitalSignageGroup: data.digitalSignageGroup.length > 0,
            digitalSignage: data.digitalSignage.length > 0,
            broadcast: data.broadcast.length > 0,
        }
    }

    const onSelect = (element: AutocompleteItem, type: AutocompleteItemType) => {
        saveHistory(element, type)
        if (p_.onSelect) {
            p_.onSelect(element, type)
        }
    }

    const saveHistory = (element: AutocompleteItem, __type: AutocompleteItemType) => {
        const findElement = allHistory.find(
            (historyItem) => historyItem.id === element.id && historyItem.__type === __type
        )

        if (!findElement) {
            allHistory.unshift({ ...element, __type })
            allHistory.slice(0, historyLength - 1)
        }

        history.digitalSignage = []
        history.digitalSignageGroup = []
        history.broadcast = []
        allHistory.forEach((historyItem) => {
            if (historyItem.__type && isHistoryType(historyItem.__type)) {
                history[historyItem.__type].push(historyItem)
            }
        })
    }

    const searchStatus = getSearchStatus(dataSource)
    const isOpenSearch = p_.isOpen && (query.name || allHistory.length > 0)

    if (p_.searchMap) {
        const filteredDisplays = displays.filter((item) => item.lat && item.lng).length > 0
        return (
            <div className={cn(styles.search, p_.isOpen && query.query ? styles.opened : '')}>
                {p_.isOpen && query.query && (
                    <div className={styles.select}>
                        {window.spconfig.map.type !== 'open-street-map' ? (
                            <div>
                                <div className={styles.title}>{translate('addresses')}</div>
                                <div className={styles.list}>
                                    {places.length > 0 &&
                                        places.map((item, index) => (
                                            <Item
                                                name={item.address}
                                                onClick={() => onSelect(item, 'places')}
                                                key={`places__item_${index}`}
                                                type="places"
                                            />
                                        ))}
                                    {places.length === 0 && (
                                        <div className={styles.empty}>{translate('noAddresses')}</div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        <div>
                            <div className={styles.title}>{translate('devices')}</div>
                            <div className={styles.list}>
                                {filteredDisplays &&
                                    displays.map((item, index) => {
                                        if (item.lng && item.lat) {
                                            return (
                                                <Item
                                                    {...item}
                                                    onClick={() => onSelect(item, 'displays')}
                                                    key={`digitalSignage__item_${index}`}
                                                    type="digitalSignage"
                                                />
                                            )
                                        } else {
                                            return null
                                        }
                                    })}
                                {!filteredDisplays && (
                                    <div className={styles.empty}>{translate('devicesNotFound')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    } else {
        return (
            <div className={cn(styles.search, isOpenSearch ? styles.opened : '')}>
                {isOpenSearch && (
                    <div className={styles.select}>
                        <div>
                            <div className={styles.title}>{translate('groups')}</div>
                            <div className={styles.list}>
                                {searchStatus.digitalSignageGroup &&
                                    dataSource.digitalSignageGroup.map((searchItem, index) => (
                                        <Item
                                            {...searchItem}
                                            onClick={() => onSelect(searchItem, 'digitalSignageGroup')}
                                            type="digitalSignageGroup"
                                            key={`digitalSignageGroup__item_${index}`}
                                        />
                                    ))}
                                {!searchStatus.digitalSignageGroup && (
                                    <div className={styles.empty}>{translate('groupsNotFound')}</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className={styles.title}>{translate('devices')}</div>
                            <div className={styles.list}>
                                {searchStatus.digitalSignage &&
                                    dataSource.digitalSignage.map((searchItem, index) => (
                                        <Item
                                            {...searchItem}
                                            onClick={() => onSelect(searchItem, 'digitalSignage')}
                                            key={`digitalSignage__item_${index}`}
                                            type="digitalSignage"
                                        />
                                    ))}
                                {!searchStatus.digitalSignage && (
                                    <div className={styles.empty}>{translate('devicesNotFound')}</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className={styles.title}>{translate('broadcasts')}</div>
                            <div className={styles.list}>
                                {searchStatus.broadcast &&
                                    dataSource.broadcast.map((searchItem, index) => (
                                        <Item
                                            {...searchItem}
                                            onClick={() => onSelect(searchItem, 'broadcast')}
                                            key={`broadcast__item_${index}`}
                                            type="broadcast"
                                        />
                                    ))}
                                {!searchStatus.broadcast && (
                                    <div className={styles.empty}>{translate('broadcastsNotFound')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

Autocomplete.defaultProps = {
    searchMap: false,
}

export default Autocomplete
